import { GridColDef, GridSortModel, GridCellParams } from '@mui/x-data-grid';
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGridModel, DataGrid } from 'src/shared/components/organisms/dataGrid';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { Box, IconButton, Tooltip } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { deleteElectronicInvoiceReceiver, getAllElectronicInvoiceReceivers } from 'src/shared/services/electronicInvoiceReceiverService';
import { toast } from 'react-toastify';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { ElectronicInvoiceReceiver } from '../models/electronicInvoiceReceiver';
import { EInvoiceReceiverSearchFilter } from '../models/eInvoiceReceiverSearchFilter';

type EInvoiceReceiverDataGridProps = {
  searchFilter: EInvoiceReceiverSearchFilter;
  searchFilterCallback: (filter: EInvoiceReceiverSearchFilter) => void;
};

export const EInvoiceReceiverDataGrid = (props: EInvoiceReceiverDataGridProps) => {
  const { searchFilter, searchFilterCallback } = props;
  const { t } = useTranslation(['invoice']);
  const { scrollIntoView, scrollRef } = useScrollIntoView();
  const isMounted = useIsMounted();

  const [rows, setRows] = useState<ElectronicInvoiceReceiver[]>([]);
  const [data, setData] = React.useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageIndex,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: searchFilter.sortCol, sort: searchFilter.sortDir }]);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
    }

    try {
      const result = await getAllElectronicInvoiceReceivers(searchFilter);
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);

        setTimeout(() => {
          setRows(
            result.data.results.map((item) => {
              return { ...item, id: item.id };
            }),
          );
        }, 100);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
      }
    }
  }, [isMounted, searchFilter]);

  const handleDeleteConfirm = async (result?: { id: string }) => {
    if (!result) {
      setPendingDeleteId(null);
      return;
    }
    try {
      await deleteElectronicInvoiceReceiver(Number(result.id));
      if (isMounted()) {
        toast.success(String(t('invoice:eInvoice.receiver.deleteSuccess')));
        fetchData();
      }
    } catch {
      if (isMounted()) {
        toast.error(String(t('invoice:eInvoice.receiver.deleteError')));
      }
    } finally {
      if (isMounted()) setPendingDeleteId(null);
    }
  };

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const columns: GridColDef[] = [
    {
      field: 'id',
      headerName: 'ID',
      sortable: true,
      flex: 1,
    },
    {
      field: 'name',
      headerName: t('invoice:eInvoice.receiver.tableHeader.name'),
      sortable: true,
      flex: 2,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.name}</>;
      },
    },
    {
      field: 'cin',
      headerName: t('invoice:eInvoice.receiver.tableHeader.cin'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.cin}</>;
      },
    },
    {
      field: 'gln',
      headerName: t('invoice:eInvoice.receiver.tableHeader.gln'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.gln}</>;
      },
    },
    {
      field: 'city',
      headerName: t('invoice:eInvoice.receiver.tableHeader.city'),
      sortable: true,
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.city}</>;
      },
    },
    {
      field: 'countryCode',
      headerName: t('invoice:eInvoice.receiver.tableHeader.countryCode'),
      sortable: true,
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.countryCode}</>;
      },
    },
    {
      field: 'linkedCount',
      headerName: t('invoice:eInvoice.receiver.tableHeader.linkedCount'),
      sortable: true,
      flex: 0.5,
      renderCell: (params: GridCellParams) => {
        const count = params.row.linkedCount ?? 0;
        return <>{count > 0 ? `${count} st` : '–'}</>;
      },
    },
    {
      field: 'actions',
      headerName: '',
      width: 56,
      sortable: false,
      renderCell: (params: GridCellParams) => {
        const count = params.row.linkedCount ?? 0;
        const isDisabled = count > 0;
        return (
          <Tooltip title={isDisabled ? t('invoice:eInvoice.receiver.deleteDisabled') : ''} disableHoverListener={!isDisabled}>
            <span>
              <IconButton size='small' disabled={isDisabled} onClick={() => setPendingDeleteId(params.row.id)}>
                <Delete fontSize='small' />
              </IconButton>
            </span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
          py: 1,
        },
      }}
      ref={scrollRef}
    >
      <DataGrid<ElectronicInvoiceReceiver>
        columns={columns}
        rows={rows}
        autoHeight
        dataModel={data}
        columnVisibilityModel={{ id: false }}
        checkboxSelection={false}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        sortModel={sortModel}
        onSortModelChange={(newSortModel) => {
          const sort = sortModel[0].sort === 'asc' ? 'desc' : 'asc';
          if (newSortModel.length) {
            setSortModel(newSortModel);
          } else {
            setSortModel([{ field: sortModel[0].field, sort }]);
          }
          searchFilterCallback({
            ...searchFilter,
            pageIndex: 0,
            sortCol: newSortModel.length ? newSortModel[0].field : sortModel[0].field,
            sortDir: newSortModel.length ? newSortModel[0].sort : sort,
          });
        }}
        selectionModel={[]}
        onSelectionModelChange={() => ConsoleHelper.log('')}
      />
      {pendingDeleteId !== null && (
        <DeleteConfirmationDialog
          open
          title={t('invoice:eInvoice.receiver.deleteTitle')}
          modaltext={t('invoice:eInvoice.receiver.deleteConfirmText')}
          value={String(pendingDeleteId)}
          confirmButtonText={t('common:button.delete')}
          confirmButtonColor='error'
          onClose={handleDeleteConfirm}
        />
      )}
    </Box>
  );
};
